import React, { useEffect, useState } from 'react'

import { observer } from 'mobx-react'
import { FieldErrors, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import cn from 'classnames'
import * as yup from 'yup'

import { yupResolver } from '@hookform/resolvers/yup'

import { checkBadWords } from 'utils'

import { toast } from 'App'
import Button from 'components/Button'
import ButtonIcon from 'components/ButtonIcon'
import ImageProfile from 'components/ImageProfile'
import UserInfoInput from 'components/UserInfoInput'
import { NFTBlock } from 'containers/Profile/components/NFTBlock'

import { useStore } from 'store'

import { ActivatedUser } from '../ActivatedUser'

import styles from './styles.module.scss'

interface IProps {
  currentUser?: boolean
}

const SignUpSchema = yup.object().shape({
  UserBIO: yup
    .string()
    .required('This field is required')
    .min(15, 'My Bio should be longer than 15 characters')
    .test(
      'badWords',
      'You are not allowed to use prohibited words',
      (value, context) => {
        return !checkBadWords(value)
      }
    ),
})

//Resize
const UserInfoBio: React.FC<IProps> = observer(({ currentUser }) => {
  const navigate = useNavigate()
  const { profile } = useStore()
  const [change, setChange] = useState(false)
  const {
    FirstName,
    LastName,
    NickName,
    UserBIO,
    OpenSeaLink,
    Id,
    ImageLink,
    Activated,
    IsStreaming,
    DeletionMark,
  } = profile.viewedUser

  useEffect(() => {
    if (!currentUser) setChange(false)
  }, [currentUser])
  // useEffect(() => {
  //   setChange(!!currentUser && !UserBIO)
  // }, [currentUser, UserBIO])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(SignUpSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  const onSubmit = async (data: Record<string, string>) => {
    await profile.onChangeUserInfo(data)
    setChange(false)
  }

  const onError = (err: FieldErrors) => {
    toast({
      type: 'warning',
      message: String(err.UserBIO?.message),
    })
  }

  const handlerChangeUserImage = async (data: File, openSeaLink?: string) => {
    await profile.changeUserImage(data, openSeaLink)
  }

  const goToUserStreamPage = () => {
    if (IsStreaming) {
      navigate(`/video_stream/${NickName}`, { state: { nickname: NickName } })
    }
  }

  return (
    <form
      className={cn({
        [styles.bioContainer]: true,
        [styles.bioContainerCurrent]: currentUser,
        [styles.bioContainer_deletedUser]: DeletionMark,
      })}
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <div className={styles.identifierBlock}>
        <div className={styles.name}>
          {!DeletionMark
            ? `${FirstName ? FirstName : ''} ${LastName ? LastName : ''}`
            : 'Deleted User'}
        </div>
        {!DeletionMark && (
          <div className={styles.nickNameBlock}>
            <div className={styles.nickName}>@{NickName}</div>
            <ActivatedUser
              activated={Activated}
              style={{ marginTop: '2px' }}
              deactivatedMode={currentUser}
              currentUser={profile.viewedUser.Id === profile.selectedUser.Id}
            />
          </div>
        )}
      </div>
      <div className={styles.imageBlock}>
        <ImageProfile
          withZoom
          image={!DeletionMark ? ImageLink : ''}
          openSeaLink={OpenSeaLink}
          currentUser={currentUser}
          onSaveImage={handlerChangeUserImage}
          userId={Id}
          isStreaming={IsStreaming}
          onClick={goToUserStreamPage}
          key={Id}
        />
      </div>
      {currentUser ? <NFTBlock onSaveImage={handlerChangeUserImage} /> : null}
      <div className={styles.titleBlock}>
        <div className={styles.title}>{currentUser ? 'My Bio' : 'Bio'}</div>
        {currentUser && (
          <ButtonIcon
            style={{ pointerEvents: change ? 'none' : 'auto' }}
            icon={'edit'}
            onClick={() => setChange(true)}
          />
        )}
      </div>
      {change ? (
        <UserInfoInput
          edit={true}
          type={'area'}
          placeholder="Bio"
          value={UserBIO}
          name={'UserBIO'}
          register={register}
          error={errors.UserBIO?.message}
          styleErrorIcon={{ padding: 0, width: '16px', height: '16px' }}
        />
      ) : (
        <div
          className={cn({
            [styles.bioBlock]: UserBIO,
          })}
        >
          {!DeletionMark ? UserBIO : 'The user is deleted'}
        </div>
      )}
      {change && (
        <div className={styles.saveBlock}>
          <div className={styles.cancelButton} onClick={() => setChange(false)}>
            Cancel
          </div>
          <Button
            type={'submit'}
            style={{
              background: 'linear-gradient(#2E2B63, #2E2B63)',
              boxShadow: '0px 7px 25px rgba(0, 0, 0, 0.0563538)',
              borderRadius: 11,
              width: '92px',
              height: '40px',
            }}
            styleText={{ color: '#ffffff' }}
            btnStyle="classic"
            text="Save"
          />
        </div>
      )}
    </form>
  )
})

export default UserInfoBio
